import { useEffect } from 'react';

const SITE = 'https://ezylab.co.kr';
const BRAND = '이지랩';
const DEFAULT_TITLE = '이지랩 — 웹·앱 개발 스튜디오';
const DEFAULT_IMAGE = `${SITE}/og-image.png`;

/** name/property 속성을 가진 meta 태그를 찾아 갱신하거나 새로 만든다. */
function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** rel 속성을 가진 link 태그를 찾아 갱신하거나 새로 만든다. */
function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * 페이지별 메타 태그를 적용한다.
 *
 * React 18 은 컴포넌트가 렌더한 <title>/<meta> 를 head 로 올려 주지 않으므로
 * DOM 을 직접 조작한다. 모든 페이지가 index.html 의 동일한 메타를 공유하면
 * 크롤러가 서로를 중복 페이지로 판단해 색인에서 제외하므로, 각 페이지가
 * 자기 제목·설명·canonical 을 선언하게 하는 것이 목적이다.
 *
 * @param {string}  title       "| 이지랩" 이 자동으로 붙는다. 생략 시 기본 제목.
 * @param {string}  description 검색 결과에 노출될 요약.
 * @param {string}  path        canonical 에 쓰일 경로. 예) "/services"
 * @param {boolean} noindex     검색 결과에서 제외할 페이지(관리·도구 화면 등).
 */
export default function Seo({ title, description, path, image, noindex = false }) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${BRAND}` : DEFAULT_TITLE;
    const url = SITE + (path || '/');

    document.title = fullTitle;

    upsertMeta('name', 'description', description);
    upsertLink('canonical', url);

    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image || DEFAULT_IMAGE);

    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);

    // noindex 페이지는 색인 대상에서 빼고, 아니면 남아 있던 지시를 지운다.
    const robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      upsertMeta('name', 'robots', 'noindex, follow');
    } else if (robots) {
      robots.remove();
    }
  }, [title, description, path, image, noindex]);

  return null;
}
